// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';

const DisclaimerPage = () => (
  <div className="min-h-screen bg-[#1C1C1A] py-16 px-4" data-testid="disclaimer-page">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-[820px] mx-auto"
    >
      <div className="flex items-center gap-3 mb-3">
        <AlertTriangle className="w-6 h-6 text-[#C9A84C]" />
        <h1 className="text-4xl font-bold text-[#E8E8E4]">Disclaimer</h1>
      </div>
      <p className="text-sm text-[#888780] mb-10">Ultimo aggiornamento: Febbraio 2026</p>

      <div className="space-y-8 text-[#888780] leading-relaxed text-base">
        <section>
          <h2 className="text-xl font-semibold text-[#E8E8E4] mb-3">Natura del servizio — Dichiarazione MiFID II</h2>
          <div className="p-5 rounded-xl" style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.3)' }}>
            <p className="font-semibold text-[#C9A84C] mb-3">AVVERTENZA IMPORTANTE — MiFID II (Direttiva 2014/65/UE)</p>
            <p>SAPIENS (finsapiens.it) è una <strong className="text-[#E8E8E4]">piattaforma di educazione finanziaria</strong>. I contenuti, i calcolatori, le guide, gli articoli del blog e qualsiasi altro materiale presente sul sito hanno <strong className="text-[#E8E8E4]">esclusivamente scopo educativo e informativo</strong>.</p>
            <p className="mt-3">I contenuti di SAPIENS <strong className="text-[#E8E8E4]">NON costituiscono e NON devono essere interpretati come</strong>:</p>
            <ul className="mt-2 space-y-1 list-disc list-inside text-sm">
              <li>Consulenza in materia di investimenti ai sensi dell'art. 4, par. 1, n. 4 della Direttiva MiFID II (2014/65/UE)</li>
              <li>Servizi di gestione di portafoglio ai sensi del D.Lgs. 58/1998 (TUF)</li>
              <li>Percorso di apprendimento personalizzato su strumenti finanziari specifici</li>
              <li>Consulenza fiscale professionale ai sensi del D.Lgs. 241/1997</li>
              <li>Consulenza legale o previdenziale di natura professionale</li>
              <li>Offerta di prodotti o servizi finanziari regolamentati</li>
            </ul>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-[#E8E8E4] mb-3">Rischi degli investimenti finanziari</h2>
          <p>Qualsiasi informazione su strumenti finanziari (ETF, azioni, obbligazioni, criptovalute, fondi pensione, ecc.) presente su SAPIENS ha finalità esclusivamente didattica. <strong className="text-[#E8E8E4]">Gli investimenti finanziari comportano rischi, inclusa la possibile perdita del capitale investito.</strong></p>
          <p className="mt-3">I rendimenti storici e le proiezioni calcolate dagli strumenti di SAPIENS <strong className="text-[#E8E8E4]">non garantiscono risultati futuri</strong> e sono soggetti a variazioni di mercato non prevedibili.</p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-[#E8E8E4] mb-3">Accuratezza dei dati fiscali</h2>
          <p>I dati fiscali (aliquote IRPEF, contributi INPS, limiti forfettari, indici ISTAT, ecc.) sono aggiornati alla data indicata su ogni contenuto. La normativa fiscale italiana è soggetta a <strong className="text-[#E8E8E4]">frequenti variazioni legislative</strong>. SAPIENS non garantisce l'accuratezza in tempo reale dei dati fiscali e non può essere ritenuta responsabile di errori derivanti da variazioni normative successive alla pubblicazione.</p>
          <p className="mt-3">I calcolatori di SAPIENS forniscono <strong className="text-[#E8E8E4]">stime indicative</strong>. Per calcoli ufficiali e vincolanti, rivolgiti a:</p>
          <ul className="mt-2 space-y-1 list-disc list-inside text-sm">
            <li>Commercialista o consulente fiscale iscritto all'Ordine</li>
            <li>CAF (Centro Assistenza Fiscale) o Patronato</li>
            <li>Consulente del lavoro iscritto all'albo</li>
            <li>Consulente finanziario indipendente iscritto all'<a href="https://www.organismocf.it" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] underline">OCF</a></li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-[#E8E8E4] mb-3">Contenuti del Blog</h2>
          <p>Gli articoli del blog di SAPIENS sono redatti dal team editoriale a scopo divulgativo. Non costituiscono pareri professionali. Ogni articolo riporta la data di pubblicazione e aggiornamento: verifica sempre l'attualità delle informazioni prima di prendere decisioni.</p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-[#E8E8E4] mb-3">Link e risorse esterne</h2>
          <p>SAPIENS può contenere link a siti di terze parti (broker, banche, istituzioni). La presenza di un link non implica raccomandazione, sponsorizzazione o endorsement del servizio o prodotto citato. SAPIENS non è responsabile del contenuto dei siti esterni.</p>
          <p className="mt-2 text-sm">Dove presenti, i <strong className="text-[#E8E8E4]">link affiliati</strong> sono chiaramente segnalati. Le eventuali commissioni ricevute non influenzano le valutazioni editoriali.</p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-[#E8E8E4] mb-3">Limitazione di responsabilità</h2>
          <p>SAPIENS, i suoi autori, collaboratori e aventi causa non possono essere ritenuti responsabili di perdite finanziarie, danni diretti o indiretti derivanti dall'utilizzo delle informazioni presenti sul sito, nei calcolatori o nelle guide, ai sensi degli artt. 1218-1229 c.c. e dell'art. 6 del D.Lgs. 70/2003.</p>
        </section>

        <div className="p-5 rounded-xl" style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.08)' }}>
          <p className="font-medium text-[#E8E8E4] mb-2">Contatti per segnalazioni</p>
          <p className="text-sm">Per segnalare imprecisioni nei contenuti: <a href="mailto:redazione@finsapiens.it" className="text-[#C9A84C]">redazione@finsapiens.it</a></p>
          <p className="text-sm mt-1">Per questioni legali: <a href="mailto:legal@finsapiens.it" className="text-[#C9A84C]">legal@finsapiens.it</a></p>
        </div>
      </div>
    </motion.div>
  </div>
);

export default DisclaimerPage;
